import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { DocFile } from "../types";
import { useMessage } from "./Message";

/**
 * Что можно импортировать в случай — то же, что принимает сервер
 * (api/src/lib/doc-files.ts): для окна выбора файла и проверки до загрузки.
 */
export const FILE_ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tif,.tiff,.heic,.heif,.docx,.doc,.rtf,.odt,.txt,.xls,.xlsx";
export const FILE_MAX_MB = 25;

/** «1,2 МБ» / «340 КБ» — размер файла в списке записей. */
export function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
  return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Страница для DOCX/текста: свои стили, листом А4 — и на экране, и в печати. */
function pageHtml(title: string, body: string): string {
  return (
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>` +
    `html{background:#f3f4f6}` +
    `body{box-sizing:border-box;max-width:210mm;min-height:100%;margin:0 auto;padding:18mm 16mm;background:#fff;` +
    `font-family:"Times New Roman",serif;font-size:14px;line-height:1.4;color:#000}` +
    `table{border-collapse:collapse;max-width:100%}td,th{border:1px solid #999;padding:3px 6px;vertical-align:top}` +
    `img{max-width:100%;height:auto}p{margin:0 0 6px}` +
    `.file-text{white-space:pre-wrap;font-family:inherit;margin:0}` +
    `@page{size:A4;margin:15mm}@media print{html{background:#fff}body{max-width:none;padding:0}}` +
    `</style></head><body>${body}</body></html>`
  );
}

/** Картинка — одна на лист, вписана в поля. */
function imageHtml(title: string, src: string): string {
  return (
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>` +
    `@page{size:A4;margin:10mm}html,body{margin:0;height:100%}` +
    `body{display:flex;align-items:center;justify-content:center}` +
    `img{max-width:100%;max-height:100%;object-fit:contain}` +
    `</style></head><body><img src="${esc(src)}" alt=""></body></html>`
  );
}

/**
 * Просмотр импортированного файла записи: PDF — встроенным просмотрщиком
 * браузера, картинка — как есть, DOCX — страницей (с таблицами и картинками),
 * DOC, RTF, ODT и TXT — текстом. «Печать» печатает только сам файл, «Скачать» —
 * сохраняет под исходным именем. TIFF, HEIC и таблицы Excel браузер не
 * показывает — у них остаётся «Скачать».
 *
 * DOCX и текст показываются во фрейме-песочнице без скриптов: ссылки и
 * разметка из чужого документа ничего не выполняют в программе.
 */
export default function FilePreview({ docId, file, onClose }: { docId: number; file: DocFile; onClose: () => void }) {
  const message = useMessage();
  const url = api.docFileUrl(docId, false, file.name);
  const frame = useRef<HTMLIFrameElement>(null);
  const [page, setPage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (file.kind !== "html" && file.kind !== "text") return;
    let alive = true;
    api
      .docFilePreview(docId)
      .then((p) => {
        if (!alive) return;
        const body = p.kind === "html" ? p.body : `<pre class="file-text">${esc(p.body)}</pre>`;
        setPage(pageHtml(file.name, body || "<p><i>В документе нет текста.</i></p>"));
      })
      .catch((err) => alive && setError((err as Error).message));
    return () => {
      alive = false;
    };
  }, [docId, file.kind, file.name]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Печать без пустых вкладок: невидимый фрейм с содержимым и его print(). */
  function printHidden(html: string) {
    const f = document.createElement("iframe");
    f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    document.body.appendChild(f);
    const w = f.contentWindow;
    const d = f.contentDocument;
    if (!w || !d) {
      f.remove();
      message("Не получилось подготовить печать — попробуйте ещё раз", "error");
      return;
    }
    d.open();
    d.write(html);
    d.close();
    const go = () => {
      w.focus();
      w.print();
      window.setTimeout(() => f.remove(), 500);
    };
    // Картинке нужно загрузиться, иначе на листе будет пусто.
    const img = d.querySelector("img");
    if (img && !img.complete) img.addEventListener("load", go, { once: true });
    else window.setTimeout(go, 60);
  }

  function print() {
    if (file.kind === "image") {
      printHidden(imageHtml(file.name, new URL(url, window.location.href).href));
      return;
    }
    // PDF, DOCX и текст уже во фрейме — печатается он сам.
    try {
      frame.current?.contentWindow?.focus();
      frame.current?.contentWindow?.print();
    } catch {
      window.open(url, "_blank");
    }
  }

  const canPrint = file.kind === "pdf" || file.kind === "image" || ((file.kind === "html" || file.kind === "text") && !!page);

  return (
    <div className="overlay file-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="file-frame">
        <div className="file-head">
          <div className="file-title" title={file.name}>
            {file.name}
            <span>{fileSize(file.size)}</span>
          </div>
          {file.kind !== "none" && (
            <div className={`button green${canPrint ? "" : " disabled"}`} onClick={canPrint ? print : undefined}>
              Печать
            </div>
          )}
          <a className="button" href={api.docFileUrl(docId, true)} download={file.name}>
            Скачать
          </a>
          <div className="close" title="Закрыть" onClick={onClose} />
        </div>
        <div className="file-body">
          {file.kind === "pdf" && <iframe ref={frame} className="file-view" src={url} title={file.name} />}
          {file.kind === "image" && (
            <div className="file-image">
              <img src={url} alt={file.name} />
            </div>
          )}
          {(file.kind === "html" || file.kind === "text") &&
            (error ? (
              <div className="file-empty">{error}</div>
            ) : page === null ? (
              <div className="file-empty">Открываем документ…</div>
            ) : (
              <iframe
                ref={frame}
                className="file-view"
                sandbox="allow-same-origin allow-modals"
                srcDoc={page}
                title={file.name}
              />
            ))}
          {file.kind === "none" && (
            <div className="file-empty">
              Файлы .{file.ext} браузер не показывает — скачайте файл и откройте его в своей программе.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
